import type { CSSProperties } from "react";

/**
 * Screen rotation + content zoom from device settings, applied at the web
 * player root (same settings seam as `videoFit` — see video-fit.ts). A screen
 * physically mounted in portrait sets `orientation` (degrees clockwise); the
 * player rotates its whole canvas and swaps the viewport dimensions. `scale`
 * (50–200, percent) zooms the rendered content on top of the fit mode.
 */

export const ORIENTATION_VALUES = [0, 90, 180, 270] as const;
export type DeviceOrientation = (typeof ORIENTATION_VALUES)[number];

export const SCALE_MIN_PERCENT = 50;
export const SCALE_MAX_PERCENT = 200;
export const DEFAULT_SCALE_PERCENT = 100;

/** Allowlist read of settings.orientation; anything else means 0 (landscape). */
export function readOrientation(settings: unknown): DeviceOrientation {
  const raw = (settings as { orientation?: unknown } | null | undefined)
    ?.orientation;
  return (ORIENTATION_VALUES as readonly unknown[]).includes(raw)
    ? (raw as DeviceOrientation)
    : 0;
}

/** Allowlist read of settings.scale (percent); invalid/absent means 100. */
export function readScale(settings: unknown): number {
  const raw = (settings as { scale?: unknown } | null | undefined)?.scale;
  if (typeof raw !== "number" || !Number.isFinite(raw)) {
    return DEFAULT_SCALE_PERCENT;
  }
  return Math.max(SCALE_MIN_PERCENT, Math.min(SCALE_MAX_PERCENT, Math.round(raw)));
}

/**
 * Style for the rotated canvas inside a full-viewport `position: relative`/
 * `fixed` box. 90/270 swap the box dimensions (dvw/dvh) and rotate about the
 * top-left corner, pre-translated so the rotated box lands back on screen;
 * 180 rotates in place.
 */
export function orientationContainerStyle(
  orientation: DeviceOrientation,
): CSSProperties {
  switch (orientation) {
    case 90:
      return {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100dvh",
        height: "100dvw",
        transform: "rotate(90deg) translateY(-100%)",
        transformOrigin: "top left",
      };
    case 180:
      return {
        position: "absolute",
        inset: 0,
        transform: "rotate(180deg)",
      };
    case 270:
      return {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100dvh",
        height: "100dvw",
        transform: "rotate(270deg) translateX(-100%)",
        transformOrigin: "top left",
      };
    default:
      return { position: "absolute", inset: 0 };
  }
}

/** Center-origin zoom for the content layer; 100 adds no transform. */
export function scaleContainerStyle(scalePercent: number): CSSProperties {
  if (scalePercent === DEFAULT_SCALE_PERCENT) return {};
  return {
    transform: `scale(${scalePercent / 100})`,
    transformOrigin: "center",
  };
}

/** True when the settings require a transform wrapper at the player root. */
export function needsDisplayTransform(
  orientation: DeviceOrientation,
  scalePercent: number,
): boolean {
  return orientation !== 0 || scalePercent !== DEFAULT_SCALE_PERCENT;
}
